import React from 'react';
import { BookmarkIcon, BookmarkFilledIcon, TrashIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { PromptLibraryItem } from './types';
import { useTheme } from '../../components/Theme/ThemeProvider';

interface PromptLibraryCardProps {
  item: PromptLibraryItem;
  isSaved: boolean;
  onToggleSaved: (prompt: string) => Promise<void> | void;
  onClick: (prompt: string, tool?: string) => void;
  isUserCreated?: boolean;
  onDelete?: () => Promise<void> | void;
  onEdit?: () => void;
}

const Spinner: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className="animate-spin">
    <circle cx="8" cy="8" r="6" stroke={color} strokeWidth="2" strokeOpacity="0.25" />
    <path d="M14 8a6 6 0 0 0-6-6" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const PromptLibraryCard: React.FC<PromptLibraryCardProps> = ({
  item,
  isSaved,
  onToggleSaved,
  onClick,
  isUserCreated,
  onDelete,
  onEdit,
}) => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const [isHovered, setIsHovered] = React.useState(false);
  const [loadingAction, setLoadingAction] = React.useState<'delete' | 'save' | null>(null);

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete || loadingAction) return;
    setLoadingAction('delete');
    try {
      await onDelete();
    } finally {
      setLoadingAction(null);
    }
  };

  const handleToggleSaved = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loadingAction) return;
    setLoadingAction('save');
    try {
      await onToggleSaved(item.prompt);
    } finally {
      setLoadingAction(null);
    }
  };
  return (
    <div
      className="flex flex-col justify-between rounded-xl p-3 cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:-translate-y-0.5"
      style={{
        backgroundColor: isDark ? '#2a2a2e' : '#ffffff',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
        fontFamily: '3MCircularTT, sans-serif',
        minHeight: '120px',
        position: 'relative',
        zIndex: isHovered ? 110 : 'auto',
      }}
      onClick={() => onClick(item.prompt, item.tool)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Title */}
      <div>
        <h3
          className="text-sm font-semibold leading-tight mb-2"
          style={{ color: '#5E95ED' }}
        >
          {item.title}
        </h3>
        {/* Prompt description */}
        <p
          className="text-xs leading-relaxed line-clamp-4"
          style={{ color: isDark ? 'var(--dark-color-text-secondary, #B2B2BD)' : '#6b7280' }}
        >
          {item.prompt}
        </p>
      </div>

      {/* Bottom row: agent type label + actions */}
      <div className="flex items-center justify-between mt-3 pt-2">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-medium uppercase tracking-wider"
            style={{ color: isDark ? 'var(--dark-color-border-color, #75757E)' : '#9ca3af' }}
          >
            {item.agentType}
          </span>
          {item.category && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded"
              style={{
                color: isDark ? '#93c5fd' : '#2563eb',
                backgroundColor: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)',
              }}
            >
              {item.category}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isUserCreated && onEdit && (
            <button
              className="p-1 rounded-md transition-colors hover:bg-blue-500/10"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              title="Edit prompt"
              disabled={!!loadingAction}
            >
              <Pencil1Icon
                width={16}
                height={16}
                style={{ color: isDark ? '#93c5fd' : '#3b82f6' }}
              />
            </button>
          )}
          {isUserCreated && onDelete && (
            <button
              className="p-1 rounded-md transition-colors hover:bg-red-500/10"
              onClick={handleDelete}
              title="Delete prompt"
              disabled={!!loadingAction}
            >
              {loadingAction === 'delete' ? (
                <Spinner size={16} color="#ef4444" />
              ) : (
                <TrashIcon
                  width={16}
                  height={16}
                  style={{ color: '#ef4444' }}
                />
              )}
            </button>
          )}
          {!isUserCreated && <button
            className="p-1 rounded-md transition-colors hover:bg-black/10 dark:hover:bg-white/10"
            onClick={handleToggleSaved}
            title={isSaved ? 'Remove from saved' : 'Save prompt'}
            disabled={!!loadingAction}
          >
            {loadingAction === 'save' ? (
              <Spinner
                size={16}
                color={isDark ? 'var(--dark-color-text, #e5e5e5)' : '#374151'}
              />
            ) : isSaved ? (
              <BookmarkFilledIcon
                width={16}
                height={16}
                style={{ color: isDark ? 'var(--dark-color-text, #e5e5e5)' : '#374151' }}
              />
            ) : (
              <BookmarkIcon
                width={16}
                height={16}
                style={{ color: isDark ? 'var(--dark-color-border-color, #75757E)' : '#9ca3af' }}
              />
            )}
          </button>}
        </div>
      </div>
    </div>
  );
};
